import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiFetch } from "./apiFetch";
import WebsiteSearch from "./WebsiteSearch";

export default function Home() {
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApiData = async () => {
      try {
        setLoading(true);
        const response = await apiFetch("/api/sources", {});
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setApiData(data);
        setError(null);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        console.error("Error fetching API data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchApiData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Headliners - Real-time News API & Live Headlines from Thousands of Sources</title>
        <meta name="description" content="Access real-time news headlines from thousands of live sources including CNN, BBC, Reuters, NYT, and more. Blazing-fast REST API for developers with free tier." />
        <link rel="canonical" href="https://headlinefeed.dev" />
      </Helmet>
      <style>
        {`
          @keyframes scroll-horizontal {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          
          .scroll-container:hover .scroll-content {
            animation-play-state: paused;
          }
        `}
      </style>
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "100vh",
          color: "white",
        }}
      >
        {/* Hero Section */}
        <Container fluid className="py-5">
          <Container>
            <Row className="justify-content-center text-center py-5">
              <Col lg={8}>
                <h1 className="display-3 fw-bold mb-4">
                  Real-time Global News Headlines API
                </h1>

                {/* Real-time Stats - Moved to top for prominence */}
                <Container className="py-4 mb-4">
                  {loading ? (
                    <Row className="text-center">
                      <Col>
                        <Spinner animation="border" variant="light" />
                        <div className="mt-3">Loading real-time data...</div>
                      </Col>
                    </Row>
                  ) : error ? (
                    <Row className="text-center">
                      <Col>
                        <Alert variant="warning">
                          Unable to fetch real-time data: {error}
                        </Alert>
                      </Col>
                    </Row>
                  ) : (
                    <Row className="text-center">
                      <Col md={3} className="mb-4">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          <div
                            className="spinner-grow text-success me-2"
                            style={{ width: "0.75rem", height: "0.75rem" }}
                          ></div>
                          <div className="h4 fw-bold text-warning mb-0">
                            LIVE
                          </div>
                        </div>
                        <div className="fs-6">Real-time Updates</div>
                      </Col>
                      <Col md={3} className="mb-4">
                        <div className="h2 fw-bold text-warning">
                          {apiData?.total_headlines?.toLocaleString() || "1M+"}
                        </div>
                        <div className="fs-5">Total Headlines</div>
                      </Col>
                      <Col md={3} className="mb-4">
                        <div className="h2 fw-bold text-warning">
                          {apiData?.total || "800+"}
                        </div>
                        <div className="fs-5">Live Sources</div>
                      </Col>
                      <Col md={3} className="mb-4">
                        <div className="h2 fw-bold text-warning">24/7</div>
                        <div className="fs-5">Continuous Updates</div>
                      </Col>
                    </Row>
                  )}
                </Container>

                <p className="lead fs-4 mb-4">
                  Get <strong>instant access</strong> to live headlines from{" "}
                  <strong>thousands of trusted sources</strong> worldwide. Our real-time
                  API delivers breaking news as it happens, updated continuously
                  24/7.
                </p>
                <div className="d-flex justify-content-center gap-3 mb-4">
                  <Badge bg="success" text="white" className="fs-6 px-3 py-2">
                    🔴 Live Data
                  </Badge>
                  <Badge bg="warning" text="dark" className="fs-6 px-3 py-2">
                    ⚡ Real-time Updates
                  </Badge>
                  <Badge bg="info" text="white" className="fs-6 px-3 py-2">
                    🌍 Thousands of Sources
                  </Badge>
                  <Badge bg="info" text="white" className="fs-6 px-3 py-2">
                    Powered by Firebase
                  </Badge>
                </div>
              </Col>
            </Row>
          </Container>
        </Container>

        {/* Search Section */}
        <WebsiteSearch />

        {/* News Sources Section */}
        {!loading && !error && apiData?.sources && (
          <Container className="py-5">
            <Row className="text-center mb-4">
              <Col>
                <h2 className="display-6 fw-bold mb-3">Live News Sources</h2>
                <p className="lead">
                  Real-time data streaming from {apiData.unique_sources} trusted
                  news sources
                </p>
                <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                  <Badge bg="info" className="px-3 py-2">
                    <span className="me-2">🔴</span>
                    Live Stream
                  </Badge>
                  <Badge bg="success" className="px-3 py-2">
                    <span className="me-2">📈</span>
                    Real-time Updates
                  </Badge>
                  <Badge bg="info" className="px-3 py-2">
                    <span className="me-2">⚡</span>
                    Instant Access
                  </Badge>
                  <Badge bg="warning" text="dark" className="px-3 py-2">
                    <span className="me-2">➕</span>
                    Growing Network
                  </Badge>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card
                  className="border-0 shadow"
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Card.Header className="bg-transparent border-0 text-center">
                    <h5 className="mb-0">
                      Active News Sources ({apiData.sources.length} sources)
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div
                      className="d-flex align-items-center py-3"
                      style={{
                        overflowX: "auto",
                        scrollBehavior: "smooth",
                        maskImage:
                          "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                        WebkitMaskImage:
                          "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                      }}
                    >
                      {/* Animated scrolling container */}
                      <div
                        className="d-flex gap-3 px-3"
                        style={{
                          animation: "scroll-horizontal 1800s linear infinite",
                          minWidth: "max-content",
                        }}
                      >
                        {[...apiData.sources, ...apiData.sources].map(
                          (source: string, index: number) => (
                            <Badge
                              key={`${source}-${index}`}
                              bg="light"
                              text="dark"
                              className="px-3 py-2 text-nowrap"
                              style={{
                                fontSize: "0.9rem",
                                minWidth: "120px",
                                textAlign: "center",
                              }}
                            >
                              {source}
                            </Badge>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Manual scroll hint */}
                    <div className="text-center pb-3">
                      <small className="text-light opacity-75">
                        <span className="me-2">🖱️</span>
                        Scroll horizontally to explore all sources
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Real-time status indicator */}
            <Row className="mt-4">
              <Col className="text-center">
                <div
                  className="d-inline-flex align-items-center gap-3 px-4 py-3 rounded-pill"
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="spinner-grow spinner-grow-sm text-danger"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-light fw-bold">LIVE</span>
                  </div>
                  <span className="text-light">
                    Real-time updates • Headlines streaming 24/7
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="spinner-grow spinner-grow-sm text-success"
                      role="status"
                      style={{ animationDelay: "0.3s" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-light fw-bold">ACTIVE</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        )}

        {/* Code Example Section */}
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card
                className="border-0 shadow"
                style={{ background: "rgba(0,0,0,0.3)" }}
              >
                <Card.Header className="bg-dark text-light">
                  <h5 className="mb-0">Quick Start Example</h5>
                </Card.Header>
                <Card.Body className="bg-dark text-light">
                  <Row>
                    <Col lg={6}>
                      <h6 className="text-warning mb-3">📝 API Requests</h6>
                      <pre
                        className="mb-0"
                        style={{ fontSize: "13px", lineHeight: "1.4" }}
                      >
                        {`# Get available sources (no auth needed)
curl https://api.headlinefeed.dev/api/sources

# Search headlines with date range (requires auth)
curl -X POST https://api.headlinefeed.dev/api/search \\
  -H 'Authorization: Bearer YOUR_API_TOKEN' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "startDate": "2025-11-01",
    "endDate": "2025-11-03",
    "title": "trump",
    "source": "wall-street-journal"
  }'

# Search recent headlines (last 3 days)
curl -X POST https://api.headlinefeed.dev/api/search \\
  -H 'Authorization: Bearer YOUR_API_TOKEN' \\
  -H 'Content-Type: application/json' \\
  -d '{"title": "trump"}'`}
                      </pre>
                    </Col>
                    <Col lg={6}>
                      <h6 className="text-success mb-3">📋 Sample Responses</h6>
                      <pre
                        className="mb-0"
                        style={{ fontSize: "13px", lineHeight: "1.4" }}
                      >
                        {`// Sources API Response
{
  "sources": [
    "cnn", "bbc", "reuters", "nytimes",
    "wall-street-journal", "bloomberg",
    "techcrunch", "arstechnica"
  ],
  "total": 45
}

// Search API Response
{
  "headlines": [
    {
      "title": "Trump Announces New Policy Initiative",
      "description": "Former president outlines comprehensive plan for economic reforms in upcoming campaign speech...",
      "source": "wall-street-journal",
      "pubDate": "2025-11-02T14:30:00Z",
      "link": "https://wsj.com/articles/..."
    },
    {
      "title": "Trump Campaign Update",
      "description": "Latest developments in the presidential campaign as polls show shifting voter preferences...",
      "source": "cnn",
      "pubDate": "2025-11-01T18:45:00Z",
      "link": "https://cnn.com/politics/..."
    }
  ],
  "total": 2,
  "searchQuery": "trump"
}`}
                      </pre>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* CTA Section */}
        <Container className="py-5">
          <Row className="justify-content-center text-center">
            <Col lg={6}>
              <h3 className="fw-bold mb-3">Ready for Real-time News Access?</h3>
              <p className="lead mb-4">
                Join developers using our live Headlines API to power their
                applications with instant news updates.
              </p>
              <Link to="/pricing" style={{ textDecoration: "none" }}>
                <Button variant="warning" size="lg" className="px-5 me-3">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/pricing" style={{ textDecoration: "none" }}>
                <Button variant="outline-light" size="lg" className="px-5">
                  View Pricing
                </Button>
              </Link>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}
